import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from './button';
import { generateReceiptPDF, downloadReceiptPDF } from '../../utils/receiptGenerator';
import { EmpresaInfo, DetalleRecibo } from '../../utils/receiptTemplates';
import { getNextReceiptNumber } from '../../utils/receiptCounter';

interface DownloadReceiptButtonProps {
  empresaInfo: EmpresaInfo;
  detalleRecibo: Omit<DetalleRecibo, 'numeroRecibo' | 'fechaEmision'>;
  fileName: string; // Nombre base del archivo (sin extensión)
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  onSuccess?: (numeroRecibo: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Componente reutilizable para descargar recibos en PDF
 * Genera automáticamente el número de recibo correlativo
 */
export function DownloadReceiptButton({
  empresaInfo,
  detalleRecibo,
  fileName,
  variant = 'ghost',
  size = 'icon',
  className = '',
  onSuccess,
  onError
}: DownloadReceiptButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    try {
      setLoading(true);

      // Generar número de recibo correlativo
      const numeroRecibo = await getNextReceiptNumber();
      
      // Completar detalle del recibo con número y fecha
      const detalleCompleto: DetalleRecibo = {
        ...detalleRecibo,
        numeroRecibo,
        fechaEmision: new Date().toISOString()
      } as DetalleRecibo;

      // Generar PDF
      const pdfBlob = await generateReceiptPDF(empresaInfo, detalleCompleto);

      // Descargar PDF
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const fullFileName = `${fileName}-${numeroRecibo}-${timestamp}`;
      downloadReceiptPDF(pdfBlob, fullFileName);

      // Callback de éxito
      if (onSuccess) {
        onSuccess(numeroRecibo);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error al generar recibo:', error);
      setLoading(false);
      
      if (onError) {
        onError(error as Error);
      } else {
        alert('Error al generar el recibo. Por favor, intenta nuevamente.');
      }
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleDownload}
      disabled={loading}
      className={className}
      title="Descargar recibo"
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
      ) : (
        <Download className="h-4 w-4" />
      )}
    </Button>
  );
}

/**
 * Variante del botón con texto
 */
export function DownloadReceiptButtonWithText({
  empresaInfo,
  detalleRecibo,
  fileName,
  buttonText = 'Descargar Recibo',
  className = '',
  onSuccess,
  onError
}: Omit<DownloadReceiptButtonProps, 'variant' | 'size'> & { buttonText?: string }) {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    try {
      setLoading(true);

      // Generar número de recibo correlativo
      const numeroRecibo = await getNextReceiptNumber();
      
      // Completar detalle del recibo con número y fecha
      const detalleCompleto: DetalleRecibo = {
        ...detalleRecibo,
        numeroRecibo,
        fechaEmision: new Date().toISOString()
      } as DetalleRecibo;

      // Generar PDF
      const pdfBlob = await generateReceiptPDF(empresaInfo, detalleCompleto);

      // Descargar PDF
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const fullFileName = `${fileName}-${numeroRecibo}-${timestamp}`;
      downloadReceiptPDF(pdfBlob, fullFileName);

      // Callback de éxito
      if (onSuccess) {
        onSuccess(numeroRecibo);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error al generar recibo:', error);
      setLoading(false);
      
      if (onError) {
        onError(error as Error);
      } else {
        alert('Error al generar el recibo. Por favor, intenta nuevamente.');
      }
    }
  };

  return (
    <Button
      onClick={handleDownload}
      disabled={loading}
      className={`bg-blue-600 hover:bg-blue-700 text-white ${className}`}
    >
      {loading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Generando...
        </>
      ) : (
        <>
          <Download className="h-4 w-4 mr-2" />
          {buttonText}
        </>
      )}
    </Button>
  );
}


